import Box from '@mui/material/Box';
import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import UniversalMediaUploaderStatus from '../../components/universal-media-uploader-status';
import { formatMediaDateTitle } from '../media/metadata-date';
import Textarea from '../../components/form-elements/textarea';
import type { MediaType } from './media-field-type';

interface Props {
  url: string;
  children?: React.ReactNode;
  caption: string;
  onCaptionChange?: (newCaption: string) => void;
  editing?: boolean;
  disabled?: boolean;
  fileSize?: number;
  fileName?: string;
  createdAt?: string | null;
  onRemove?: () => void;
  onClick?: () => void;
  fileType?: MediaType;
  thumbnailStyle?: 'cover' | 'contain';
  avatarSize?: 'default' | 'large';
}

const readOnlyCaptionSx: SxProps<Theme> = {
  pt: 1,
  whiteSpace: 'pre-wrap',
  color: 'text.secondary',
  fontSize: (theme) => theme.typography.body2.fontSize,
};

const CaptionedThumbnail: React.FC<Props> = ({
  url,
  children,
  caption,
  onCaptionChange,
  editing = false,
  disabled = false,
  fileSize,
  fileName,
  createdAt,
  onRemove,
  onClick,
  fileType,
  thumbnailStyle,
  avatarSize = 'large',
}: Props) => {
  const [currentCaption, setCurrentCaption] = useState<string>(caption ?? '');

  useEffect(() => {
    setCurrentCaption(caption ?? '');
  }, [caption]);

  const handleCaptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCurrentCaption(e.target.value);
    if (onCaptionChange) {
      onCaptionChange(e.target.value);
    }
  };

  const handleCaptionBlur = () => {
    const trimmed = currentCaption.trimEnd();
    setCurrentCaption(trimmed);
    if (onCaptionChange) {
      onCaptionChange(trimmed);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <UniversalMediaUploaderStatus
        name={fileName ?? formatMediaDateTitle(createdAt)}
        size={fileSize}
        thumbnailURL={url}
        onClose={editing && !disabled ? onRemove : undefined}
        onClick={onClick}
        avatarSize={avatarSize}
        fileType={fileType}
        thumbnailStyle={thumbnailStyle}
      />
      {children}
      {editing && !disabled && (
        <Box sx={{ p: 2, width: '100%' }}>
          <Textarea
            value={currentCaption || ''}
            onChange={handleCaptionChange}
            onBlur={handleCaptionBlur}
            label="Caption"
            size="small"
            minRows={1}
            maxRows={5}
            sx={{ width: '100%', '& .MuiInputLabel-root': { left: '-3px' } }}
            inputProps={{ 'aria-label': 'Caption' }}
          />
        </Box>
      )}
      {(!editing || disabled) && currentCaption && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography variant="body2" sx={readOnlyCaptionSx}>
            {currentCaption}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CaptionedThumbnail;
